import type { KnowledgeBaseApi } from '@liks/backend-svc-api/esm/apis/KnowledgeBaseApi';
import type { KnowledgeBase } from '@liks/backend-svc-api/esm/models/index';
import type { KnowledgeBaseWithIdentity } from '../types/knowledge';
import { isAccessibleKnowledgeBase } from './knowledgeServiceGuards';

export async function listDatabases(
  databaseApi: KnowledgeBaseApi,
): Promise<KnowledgeBaseWithIdentity[]> {
  const knowledgeBases =
    await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseResourceListAccessibleKnowledgeBases(
      { limit: 1000 },
    );

  return knowledgeBases.filter(isAccessibleKnowledgeBase);
}

export async function createDatabase(
  databaseApi: KnowledgeBaseApi,
  name: string,
  description: string,
  icon?: string,
): Promise<KnowledgeBaseWithIdentity> {
  const created =
    await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseResourceCreateKnowledgeBase(
      {
        knowledgeBase: {
          name: name.trim(),
          description: description.trim() || undefined,
          icon: icon?.trim() || undefined,
        },
      },
    );

  if (!isAccessibleKnowledgeBase(created)) {
    throw new Error('Backend returned an invalid knowledge database');
  }

  return created;
}

export async function getDatabase(
  databaseApi: KnowledgeBaseApi,
  id: string,
): Promise<KnowledgeBaseWithIdentity> {
  const knowledgeBase =
    await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseResourceReadKnowledgeBase(
      { id },
    );

  if (!isAccessibleKnowledgeBase(knowledgeBase)) {
    throw new Error('Backend returned an invalid knowledge database');
  }

  return knowledgeBase;
}

export async function updateDatabase(
  databaseApi: KnowledgeBaseApi,
  database: KnowledgeBase,
  updates: Pick<KnowledgeBase, 'name' | 'description' | 'icon'>,
): Promise<KnowledgeBaseWithIdentity> {
  if (typeof database.id !== 'string' || database.id.length === 0) {
    throw new Error('Cannot update knowledge database without id');
  }

  const updated =
    await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseResourceUpdateKnowledgeBase(
      {
        id: database.id,
        knowledgeBase: {
          id: database.id,
          name: updates.name?.trim(),
          description: updates.description?.trim() || undefined,
          icon: updates.icon?.trim() || undefined,
        },
      },
    );

  if (!isAccessibleKnowledgeBase(updated)) {
    throw new Error('Backend returned an invalid knowledge database');
  }

  return updated;
}

export async function deleteDatabase(
  databaseApi: KnowledgeBaseApi,
  id: string,
): Promise<void> {
  await databaseApi.deLiksKnowledgebaseBoundaryKnowledgeBaseResourceDeleteKnowledgeBase(
    { id },
  );
}
